import clamp from 'clamp';
import type { LayoutRectangle, ViewStyle } from 'react-native';

import { assertNever } from '@gitbook/util-ts';

import type { LayoutDimensions } from '../Responsive';
import type { PopoverAlignment, PopoverPlacement } from './types';

export interface LayoutDimensionsRange {
    minWidth: number;
    maxWidth: number;
    minHeight: number;
    maxHeight: number;
}

export type PopoverDimensionsRange = LayoutDimensions & LayoutDimensionsRange;

interface PopoverStaticDimensionsRange extends PopoverDimensionsRange {
    width: number;
}

export interface PopoverPositionOptions {
    /**
     * Range of sizes allowed for the popover.
     * It can be over-ruled by a range passed as part of `popover`.
     * @default size of the popover
     */
    range: LayoutDimensionsRange;

    /**
     * Should the popover cover the anchor?
     * If false, the popover will be positioned next to the anchor.
     * @default false
     */
    cover: boolean;

    /**
     * Spacing between the popover and the anchor.
     * @default 0
     */
    spacingWithAnchor: number;

    /**
     * Spacing between the popover and the window border.
     * @default 0
     */
    spacingWithWindow: number;

    /**
     * Placements allowed for the popover.
     * @default ['top', 'bottom', 'left', 'right']
     */
    allowedPlacements: PopoverPlacement[];

    /**
     * Alignments allowed for the popover.
     * @default ['start', 'middle', 'end']
     */
    allowedAlignments: PopoverAlignment[];

    /**
     * Best placement to pick if possible.
     */
    preferredPlacement: PopoverPlacement;

    /**
     * Best alignment to pick if possible.
     */
    preferredAlignment: PopoverAlignment;

    /**
     * Offset to add to the position of the popover.
     */
    offset: {
        top?: number;
        left?: number;
        right?: number;
        bottom?: number;
    };
}

/**
 * Compute a smart position for a popover around a given anchor.
 */
export function computePopoverPosition(
    anchor: LayoutRectangle,
    popover: LayoutDimensions & Partial<PopoverDimensionsRange>,
    windowSize: LayoutDimensions,
    partialOptions: Partial<PopoverPositionOptions>
): [ViewStyle, PopoverPlacement, PopoverAlignment] {
    const options: PopoverPositionOptions = {
        spacingWithAnchor: 0,
        spacingWithWindow: 0,
        allowedPlacements: ['bottom', 'top', 'right', 'left'],
        allowedAlignments: ['start', 'middle', 'end'],
        cover: false,
        preferredPlacement: 'bottom',
        preferredAlignment: 'start',
        range: {
            minWidth: popover.width,
            maxWidth: popover.width,
            minHeight: popover.height,
            maxHeight: popover.height,
        },
        offset: {
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
        },
        ...partialOptions,
    };

    const popoverRange: PopoverStaticDimensionsRange = {
        ...options.range,
        ...popover,
    };
    popoverRange.width = clamp(popoverRange.minWidth, popoverRange.width, popoverRange.maxWidth);
    popoverRange.height = clamp(
        popoverRange.minHeight,
        popoverRange.height,
        popoverRange.maxHeight
    );

    const [placement, alignment] = decidePopoverPlacementAndPopoverAlignment(
        anchor,
        popoverRange,
        windowSize,
        options
    );

    const style = computePopoverPlacementStyle(
        anchor,
        popoverRange,
        windowSize,
        placement,
        alignment,
        options
    );

    return [
        lockPopoverInScreen(
            {
                ...style,
                // @ts-ignore - transformOrigin works on RNW
                transformOrigin: computeTransformOrigin(placement, alignment, options),
            },
            popoverRange,
            windowSize,
            options
        ),
        placement,
        alignment,
    ];
}

function decidePopoverPlacementAndPopoverAlignment(
    anchor: LayoutRectangle,
    popover: PopoverStaticDimensionsRange,
    windowSize: LayoutDimensions,
    options: PopoverPositionOptions
): [PopoverPlacement, PopoverAlignment] {
    const {
        spacingWithAnchor,
        allowedPlacements,
        allowedAlignments,
        preferredPlacement,
        preferredAlignment,
    } = options;

    const getScoreForOffset = (offset: number, elementSize: number, screenSize: number) => {
        if (offset < spacingWithAnchor) {
            return offset / screenSize;
        }

        const outsideScreen = screenSize - (offset + elementSize + spacingWithAnchor);

        if (outsideScreen < 0) {
            return outsideScreen / screenSize;
        }
        return 0;
    };

    const combinaisons: Array<[PopoverPlacement, PopoverAlignment, number, ViewStyle]> = [];
    allowedPlacements.forEach((placement) => {
        allowedAlignments.forEach((alignment) => {
            const position = computePopoverPlacementStyle(
                anchor,
                popover,
                windowSize,
                placement,
                alignment,
                options
            );

            let score = 0;

            if (typeof position.top === 'number') {
                score += getScoreForOffset(position.top, popover.height, windowSize.height);
            }
            if (typeof position.bottom === 'number') {
                score += getScoreForOffset(position.bottom, popover.height, windowSize.height);
            }
            if (typeof position.right === 'number') {
                score += getScoreForOffset(position.right, popover.width, windowSize.width);
            }
            if (typeof position.left === 'number') {
                score += getScoreForOffset(position.left, popover.width, windowSize.width);
            }

            combinaisons.push([placement, alignment, score, position]);
        });
    });

    // Sort the results by score to get the best ones first
    combinaisons.sort((a, b) => b[2] - a[2]);

    const findBestOne = (
        placement: PopoverPlacement | null,
        alignment: PopoverAlignment | null
    ) => {
        const matching = combinaisons.filter((result) => {
            if (placement && result[0] !== placement) {
                return false;
            }

            if (alignment && result[1] !== alignment) {
                return false;
            }

            return true;
        });

        const bestMatch = matching[0];

        // Return if we have a perfect match
        if (bestMatch && bestMatch[2] === 0) {
            return bestMatch;
        }

        return null;
    };

    const bestOne =
        findBestOne(preferredPlacement, preferredAlignment) ||
        findBestOne(preferredPlacement, null) ||
        findBestOne(null, preferredAlignment) ||
        combinaisons[0];

    return [bestOne[0], bestOne[1]];
}

function computePopoverPlacementStyle(
    anchor: LayoutRectangle,
    popover: PopoverStaticDimensionsRange,
    windowSize: LayoutDimensions,
    placement: PopoverPlacement,
    alignment: PopoverAlignment,
    options: PopoverPositionOptions
): ViewStyle {
    const { cover, spacingWithAnchor } = options;

    switch (placement) {
        case 'bottom':
            return {
                top: anchor.y + (cover ? 0 : anchor.height + spacingWithAnchor),
                ...computePopoverAlignmentStyle(
                    alignment,
                    ['left', 'right'],
                    anchor.x,
                    anchor.width,
                    popover.width,
                    windowSize.width
                ),
            };
        case 'top':
            return {
                bottom:
                    windowSize.height -
                    (anchor.y + (cover ? anchor.height : 0)) +
                    spacingWithAnchor,
                ...computePopoverAlignmentStyle(
                    alignment,
                    ['left', 'right'],
                    anchor.x,
                    anchor.width,
                    popover.width,
                    windowSize.width
                ),
            };
        case 'left':
            return {
                right: windowSize.width - anchor.x + spacingWithAnchor - (cover ? anchor.width : 0),
                ...computePopoverAlignmentStyle(
                    alignment,
                    ['top', 'bottom'],
                    anchor.y,
                    anchor.height,
                    popover.height,
                    windowSize.height
                ),
            };
        case 'right':
            return {
                left: anchor.x + (cover ? 0 : anchor.width) + spacingWithAnchor,
                ...computePopoverAlignmentStyle(
                    alignment,
                    ['top', 'bottom'],
                    anchor.y,
                    anchor.height,
                    popover.height,
                    windowSize.height
                ),
            };
        default:
            assertNever(placement);
    }
}

function computePopoverAlignmentStyle(
    alignment: PopoverAlignment,
    [startProp, endProp]: [string, string],
    offsetValue: number,
    anchorSize: number,
    popoverSize: number,
    screenSize: number
): ViewStyle {
    switch (alignment) {
        case 'start':
            return {
                [startProp]: offsetValue,
            };
        case 'middle':
            return {
                [startProp]: offsetValue - (popoverSize - anchorSize) / 2,
            };
        case 'end':
            return {
                [endProp]: screenSize - offsetValue - anchorSize,
            };
        default:
            assertNever(alignment);
    }
}

function computeTransformOrigin(
    placement: PopoverPlacement,
    alignment: PopoverAlignment,
    options: PopoverPositionOptions
): string {
    const { cover } = options;

    const centerOrigin = alignment === 'middle' ? 'center' : null;
    const horizontalOrigin = centerOrigin || (alignment === 'start' ? 'left' : 'right');
    const verticalOrigin = centerOrigin || (alignment === 'start' ? 'top' : 'bottom');

    switch (placement) {
        case 'bottom':
            return `${cover ? 'center' : horizontalOrigin} top`;
        case 'top':
            return `${cover ? 'center' : horizontalOrigin} bottom`;
        case 'left':
            return `right ${cover ? 'center' : verticalOrigin}`;
        case 'right':
            return `left ${cover ? 'center' : verticalOrigin}`;
        default:
            assertNever(placement);
    }
}

/**
 * Ensure the style will not render the popover outside the screen.
 */
function lockPopoverInScreen(
    style: ViewStyle,
    popover: PopoverStaticDimensionsRange,
    windowRect: LayoutDimensions,
    options: PopoverPositionOptions
): ViewStyle {
    const { spacingWithAnchor, spacingWithWindow } = options;

    const result = {
        minWidth: popover.minWidth,
        minHeight: popover.minHeight,
        ...style,
    };

    const clampInDirection = (
        offsetProperty: 'left' | 'right' | 'bottom' | 'top',
        sizeProperty: 'width' | 'height',
        minSizeProperty: 'minWidth' | 'minHeight',
        maxSizeProperty: 'maxWidth' | 'maxHeight'
    ) => {
        let offset = style[offsetProperty];
        const popoverSize = popover[sizeProperty];
        const minSize = popover[minSizeProperty];
        const windowSize = windowRect[sizeProperty];
        let maxSize = popover[maxSizeProperty];

        if (typeof offset === 'number') {
            offset = offset + (options.offset[offsetProperty] || 0);

            // Ensure there is a spacing with the window
            offset = clamp(offset, spacingWithWindow, windowSize - popoverSize - spacingWithWindow);

            // Limit the max-size to avoid going off-screen
            maxSize = clamp(minSize, windowSize - offset - spacingWithAnchor, maxSize);
            result[maxSizeProperty] = maxSize;

            // Then limit the size if it actually goes off-screen
            if (offset + popoverSize > windowSize) {
                result.width = clamp(minSize, windowSize - offset - spacingWithAnchor, maxSize);
            }

            result[offsetProperty] = offset;
        }
    };

    clampInDirection('left', 'width', 'minWidth', 'maxWidth');
    clampInDirection('right', 'width', 'minWidth', 'maxWidth');
    clampInDirection('top', 'height', 'minHeight', 'maxHeight');
    clampInDirection('bottom', 'height', 'minHeight', 'maxHeight');

    return result;
}
